import { Link } from 'react-router-dom'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Task } from '@/types/entities'
import { TaskStatusBadge } from './TaskStatusBadge'
import { getTaskTypeLabel } from '../hooks/useTasks'
import { formatDistanceToNow } from 'date-fns'
import { Eye, RotateCcw, XCircle, StopCircle } from 'lucide-react'

interface TaskTableProps {
  tasks: Task[]
  onViewTask: (task: Task) => void
  onCancelTask: (taskId: string) => void
  onRetryTask: (taskId: string) => void
  isLoading?: boolean
}

export function TaskTable({
  tasks,
  onViewTask,
  onCancelTask,
  onRetryTask,
  isLoading,
}: TaskTableProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-pulse text-muted-foreground">Loading tasks...</div>
      </div>
    )
  }

  if (tasks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <p className="text-lg">No tasks found</p>
        <p className="text-sm">Tasks will appear here when artworks are published or unpublished.</p>
      </div>
    )
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Type</TableHead>
          <TableHead>Artwork</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Created</TableHead>
          <TableHead>Duration</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {tasks.map((task) => (
          <TableRow key={task.id}>
            <TableCell className="font-medium">
              {getTaskTypeLabel(task.type)}
            </TableCell>
            <TableCell>
              {task.artworkId ? (
                <Link
                  to={`/app/artworks/${task.artworkId}`}
                  className="text-primary hover:underline"
                >
                  {task.artworkName || `Artwork #${task.artworkId}`}
                </Link>
              ) : (
                <span className="text-muted-foreground">N/A</span>
              )}
            </TableCell>
            <TableCell>
              <TaskStatusBadge status={task.status} />
            </TableCell>
            <TableCell className="text-muted-foreground">
              {formatDistanceToNow(new Date(task.createdAt), { addSuffix: true })}
            </TableCell>
            <TableCell className="text-muted-foreground">
              {task.completedAt
                ? formatDistanceToNow(new Date(task.createdAt), {
                    includeSeconds: true,
                  }).replace('about ', '')
                : task.startedAt
                ? 'In progress...'
                : 'Waiting'}
            </TableCell>
            <TableCell className="text-right">
              <div className="flex items-center justify-end gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onViewTask(task)}
                  title="View details"
                >
                  <Eye className="h-4 w-4" />
                </Button>
                {task.status === 'RUNNING' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onCancelTask(task.id)}
                    title="Stop task"
                    className="text-destructive hover:text-destructive"
                  >
                    <StopCircle className="h-4 w-4" />
                  </Button>
                )}
                {task.status === 'PENDING' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onCancelTask(task.id)}
                    title="Cancel task"
                  >
                    <XCircle className="h-4 w-4" />
                  </Button>
                )}
                {task.status === 'FAILED' && task.retryCount < task.maxRetries && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onRetryTask(task.id)}
                    title="Retry task"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
